import type {
  RouteMonitoringVisit,
  RouteMonitoringVisitShipping,
} from '../models';
import type { ShippingForRouting } from '@/pages/Business/pages/Shippings/models';
import {
  getAddressForShipping,
  isPickupShipping,
} from '@/pages/Business/pages/Shippings/utils/shippingAddress';
import { resolveRoutePointLatLong } from '@/pages/Business/pages/Shippings/pages/RouteBuilder/utils/map-route-monitoring-visits-to-route-points/resolve-route-point-lat-long';
import { parseCoordFromMonitoring } from '@/pages/Business/pages/Shippings/pages/RouteBuilder/utils/parse-monitoring-coord';

export interface ResolvedVisitAddress {
  streetName: string | null;
  doorNumber: string | null;
  apartmentNumber: string | null;
  lat: number | null;
  long: number | null;
}

export interface ResolvedVisitShipping {
  number: number;
  address: ResolvedVisitAddress | null;
  isPickup: boolean;
  statusLabel?: string;
  serviceLabel?: string;
}

function toNumberOrNull(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toResolvedAddress(
  addr:
    | {
        streetName?: string | null;
        doorNumber?: string | null;
        apartmentNumber?: string | null;
        lat?: number | string | null;
        long?: number | string | null;
      }
    | null
    | undefined
): ResolvedVisitAddress | null {
  if (!addr) return null;
  return {
    streetName: addr.streetName ?? null,
    doorNumber: addr.doorNumber ?? null,
    apartmentNumber: addr.apartmentNumber ?? null,
    lat: toNumberOrNull(addr.lat),
    long: toNumberOrNull(addr.long),
  };
}

export function isPickupVisitShipping(
  raw: RouteMonitoringVisitShipping | undefined
): boolean {
  if (!raw) return false;
  if (raw.visitType === 'PICKUP') return true;
  const name = (
    raw.commercialService?.name ||
    raw.commercialService?.commercialName ||
    raw.serviceVisitTypeName ||
    raw.serviceType ||
    ''
  ).toLowerCase();
  return name.includes('retiro') || name.includes('pickup');
}

/**
 * Resolves the address (and pickup flag) for every shipping of a visit.
 *
 * The route detail endpoint only returns a single `visit.address` (the commerce
 * address for pickups). When the per-shipping data is available in
 * `enrichedByNumber`, we pick the correct address with `getAddressForShipping`
 * (origin for pickups, destination otherwise). Otherwise we fall back to
 * `visit.address` while still flagging pickups from the route metadata.
 */
export function resolveVisitShippingEntries(
  visit: RouteMonitoringVisit,
  enrichedByNumber: Map<number, ShippingForRouting>
): ResolvedVisitShipping[] {
  const rawShippings = (visit.shippings ?? []).filter(
    (shipping): shipping is RouteMonitoringVisitShipping =>
      shipping?.number != null && shipping.number > 0
  );

  const fallbackAddress = toResolvedAddress(visit.address);

  if (rawShippings.length === 0) {
    if (visit.shippingNumber > 0) {
      const enriched = enrichedByNumber.get(visit.shippingNumber);
      if (enriched) {
        return [
          {
            number: visit.shippingNumber,
            address: toResolvedAddress(getAddressForShipping(enriched)),
            isPickup: isPickupShipping(enriched),
          },
        ];
      }
      return [
        {
          number: visit.shippingNumber,
          address: fallbackAddress,
          isPickup: false,
        },
      ];
    }
    return [];
  }

  return rawShippings.map((raw) => {
    const number = raw.number as number;
    const enriched = enrichedByNumber.get(number);
    if (enriched) {
      return {
        number,
        address: toResolvedAddress(getAddressForShipping(enriched)),
        isPickup: isPickupShipping(enriched),
      };
    }
    return {
      number,
      address: fallbackAddress,
      isPickup: isPickupVisitShipping(raw),
    };
  });
}

/**
 * Resolves the lat/long used to focus the visit on the map, preferring the
 * geo of the resolved (pickup-aware) shipping address when available.
 */
export function resolveVisitDisplayLatLong(
  visit: RouteMonitoringVisit,
  entries: ResolvedVisitShipping[],
  fallbackShipping: ShippingForRouting | undefined
): { lat: number; long: number } {
  const visitLat = toNumberOrNull(visit.lat);
  const visitLong = toNumberOrNull(visit.long);
  const hasVisitCoords =
    visitLat != null && visitLong != null && !(visitLat === 0 && visitLong === 0);

  if (visit.coordinatesChanged && hasVisitCoords) {
    return { lat: visitLat, long: visitLong };
  }

  const withGeo = entries.find(
    (entry) =>
      entry.address?.lat != null &&
      entry.address?.long != null &&
      Number.isFinite(entry.address.lat) &&
      Number.isFinite(entry.address.long) &&
      !(entry.address.lat === 0 && entry.address.long === 0)
  );

  if (withGeo && withGeo.address) {
    return { lat: withGeo.address.lat as number, long: withGeo.address.long as number };
  }

  if (hasVisitCoords) {
    return { lat: visitLat, long: visitLong };
  }

  const addrLat = parseCoordFromMonitoring(visit.address?.lat ?? null);
  const addrLong = parseCoordFromMonitoring(visit.address?.long ?? null);
  if (addrLat != null && addrLong != null) {
    return { lat: addrLat, long: addrLong };
  }

  return resolveRoutePointLatLong(visit, fallbackShipping);
}
